import React from "react";
import styled from "@emotion/styled";
import { useHistory } from "react-router-dom";
import { connect } from "react-redux";
import { getMenuList, getUserRoleId } from "redux/selectors";
import { ReactComponent as AppraisalPending } from "../../assets/appraisal-pending.svg";
import { ReactComponent as BillApproval } from "../../assets/bill-approval.svg";
import { ReactComponent as LeaveApproval } from "../../assets/leave-approval.svg";
import { ReactComponent as PfaApproval } from "../../assets/pfa-approval.svg";
import { ReactComponent as TimesheetApproval } from "../../assets/timesheet-approval.svg";

// ── Wrapper ──────────────────────────────────────────────────────────────────
const Wrapper = styled.div`
  height: 100%;
  padding: 15px;
  box-shadow: 0px 4px 30px 0px #0000001f;
  border-radius: 8px;
  background: #fff;
  overflow-y: auto;
`;

// ── Header row ───────────────────────────────────────────────────────────────
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const Header = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
`;

// ── Skeleton ─────────────────────────────────────────────────────────────────
const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 12px;
  margin-bottom: 8px;
`;

const shimmer = `
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite;
  @keyframes shimmer {
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const SkeletonCircle = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
  ${shimmer}
`;

const SkeletonText = styled.div`flex: 1;`;

const SkeletonLine = styled.div`
  height: ${({ height }) => height || "12px"};
  width: ${({ width }) => width || "100%"};
  border-radius: 4px;
  margin-bottom: 6px;
  ${shimmer}
`;

// ── Item row — matches Figma exactly ─────────────────────────────────────────
const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #ffffff;
  margin-bottom: 4px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #c2fae5;
  }

  &:hover > div:first-of-type {
    background: #ffffff;
  }
`;

const IconCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #c2fae5;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: relative;
  transition: background 0.2s ease;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const Badge = styled.span`
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #e53e3e;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  line-height: 1;
`;

const TextBlock = styled.div`flex: 1;`;

const Title = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ hasCount }) => (hasCount ? "#0066ff" : "#1a1a2e")};
  margin-bottom: 2px;
  &:hover { text-decoration: ${({ hasCount }) => (hasCount ? "underline" : "none")}; }
`;

const SubText = styled.div`
  font-size: 11px;
  color: #636980;
`;

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  gap: 12px;
`;

const EmptyTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
`;

const EmptySubText = styled.div`
  font-size: 12px;
  color: #636980;
  text-align: center;
  max-width: 200px;
  line-height: 1.5;
`;

// Inline SVG — no extra asset import needed
const EmptyIllustration = () => (
  <svg
    width="120"
    height="100"
    viewBox="0 0 120 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Bell body */}
    <ellipse cx="60" cy="88" rx="14" ry="4" fill="#e2e8f0" />
    <path
      d="M60 14C60 14 38 24 38 54V70H82V54C82 24 60 14 60 14Z"
      fill="#c2fae5"
      stroke="#3a5068"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    {/* Bell top dot */}
    <circle cx="60" cy="13" r="4" fill="#3a5068" />
    {/* Bell clapper */}
    <path
      d="M53 70C53 73.866 56.134 77 60 77C63.866 77 67 73.866 67 70"
      stroke="#3a5068"
      strokeWidth="2"
      fill="none"
      strokeLinecap="round"
    />
    {/* Checkmark badge */}
    <circle cx="88" cy="28" r="14" fill="#c2fae5" stroke="#fff" strokeWidth="2" />
    <path
      d="M82 28L86 32L94 24"
      stroke="#3a5068"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    {/* Small sparkles */}
    <circle cx="28" cy="38" r="3" fill="#c2fae5" />
    <circle cx="22" cy="52" r="2" fill="#e2e8f0" />
    <circle cx="34" cy="62" r="2" fill="#c2fae5" />
  </svg>
);

// ── Helpers ───────────────────────────────────────────────────────────────────
const formatDate = (isoString) => {
  if (!isoString) return "-";
  try {
    const date = new Date(isoString);
    const day = date.getDate();
    const suffix =
      day % 10 === 1 && day !== 11 ? "st"
      : day % 10 === 2 && day !== 12 ? "nd"
      : day % 10 === 3 && day !== 13 ? "rd"
      : "th";
    const month = date.toLocaleString("en-US", { month: "short" });
    const year = date.getFullYear();
    const time = date.toLocaleString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `Last update: ${day}${suffix} ${month}, ${year} ; ${time}`;
  } catch {
    return "-";
  }
};

// ── Config ────────────────────────────────────────────────────────────────────
const NOTIFICATION_CONFIG = [
  {
    id: "timesheet",
    Icon: TimesheetApproval,
    title: "pending timesheet approval",
    navigateTo: "/approvals",
    tabId: "timesheet_approvals",
    selectedItem: null,
    accessKey: "timesheetapprovals",
  },
  {
    id: "pfa",
    Icon: PfaApproval,
    title: "pending PFA approval",
    navigateTo: "/approvals",
    tabId: "pfa_approval",
    selectedItem: null,
    accessKey: "PFAapprovals",
  },
  {
    id: "billing",
    Icon: BillApproval,
    title: "pending bill approval",
    navigateTo: "/approvals",
    tabId: "billing_approval",
    selectedItem: null,
    accessKey: "billingapprovals",
  },
  {
    id: "leave",
    Icon: LeaveApproval,
    title: "pending leave approval",
    navigateTo: "/approvals",
    tabId: "leave_approvals",
    selectedItem: null,
    accessKey: "approvals",
  },
  {
    id: "appraisal",
    Icon: AppraisalPending,
    title: "pending appraisal review",
    navigateTo: "/performancemanagement",
    tabId: "managerappraisal",
    selectedItem: "performance-management",
    accessKey: "managerappraisal",
  },
];

// ── Skeleton component ────────────────────────────────────────────────────────
const NotificationSkeleton = () => (
  <>
    {[1, 2, 3, 4, 5].map((i) => (
      <SkeletonRow key={i}>
        <SkeletonCircle />
        <SkeletonText>
          <SkeletonLine width="75%" height="13px" />
          <SkeletonLine width="45%" height="10px" />
        </SkeletonText>
      </SkeletonRow>
    ))}
  </>
);

// ── Main component ────────────────────────────────────────────────────────────
const MyNotifications = ({
  pendingCounts = {},
  lastUpdated = {},
  isLoading = false,
  menuList,
  userRoleId,
}) => {
  const history = useHistory();

  let Menulist = menuList?.[userRoleId] || Object.values(menuList || {})[0] || [];
  Menulist = Array.isArray(Menulist) ? Menulist : [];

  const hasAccess = (accessKey) => {
    if (!accessKey) return true;
    return Menulist.some((item) => item.key === accessKey && item.access);
  };

  const visibleNotifications = NOTIFICATION_CONFIG.filter((n) =>
    hasAccess(n.accessKey)
  );

  // Only show items that have a count > 0
  const notificationsWithCount = visibleNotifications.filter(
    (n) => (pendingCounts[n.id] ?? 0) > 0
  );

  const handleClick = (n) => {
    if (n.tabId) {
      history.push(n.navigateTo, {
        tabId: n.tabId,
        ...(n.selectedItem && { selectedItem: n.selectedItem }),
      });
    } else {
      history.push(n.navigateTo);
    }
  };

  const getCount = (id) => pendingCounts[id] ?? 0;
  const getLastUpdated = (id) =>
    lastUpdated[id] ? formatDate(lastUpdated[id]) : "Last update: -";

  return (
    <Wrapper>
      <HeaderRow>
        <Header>My Notification</Header>
      </HeaderRow>

      {isLoading ? (
        <NotificationSkeleton />
      ) : notificationsWithCount.length === 0 ? (
        // No pending approvals (no access or all counts are 0)
        <EmptyContainer>
          <EmptyIllustration />
          <EmptyTitle>All Caught Up!</EmptyTitle>
          <EmptySubText>No pending approvals at the moment. Check back later.</EmptySubText>
        </EmptyContainer>
      ) : (
        notificationsWithCount.map((n) => {
          const count = getCount(n.id);
          const { Icon } = n;
          return (
            <ItemRow key={n.id} onClick={() => handleClick(n)}>
              <IconCircle>
                <Icon />
                <Badge>{count}</Badge>
              </IconCircle>
              <TextBlock>
                <Title hasCount>
                  You have ({count}) {n.title}
                </Title>
                <SubText>{getLastUpdated(n.id)}</SubText>
              </TextBlock>
            </ItemRow>
          );
        })
      )}
    </Wrapper>
  );
};

const mapStateToProps = (state) => ({
  menuList: getMenuList(state),
  userRoleId: getUserRoleId(state),
});

export default connect(mapStateToProps, null)(MyNotifications);